'use client';

import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react';

/**
 * MagneticCta — magnetic-feel cursor wrapper for primary CTAs.
 *
 * Generalized from `src/components/faq/FaqMagneticCta.tsx` (the Wave 2
 * prototype). When the cursor enters within `radius` pixels of the
 * wrapper's center, the wrapper translates toward the cursor up to
 * `pull` pixels. Outside the radius, the transform smoothly resets.
 *
 * Implementation notes:
 *   - `pointermove` is delegated to `window` (not the wrapper) so the
 *     pull engages while the cursor is *near* the CTA rather than only
 *     while it's hovering the element itself. This is the published
 *     "magnetic" feel — the button gently reaches toward the cursor.
 *   - All position writes are throttled through a single
 *     `requestAnimationFrame` callback to avoid layout thrash on
 *     fast pointer streams.
 *   - The effect is fully gated on `prefers-reduced-motion: reduce`
 *     (skill §4 requirement). Reduced-motion users get the children
 *     unchanged, with no listeners attached.
 *   - The wrapper renders an `inline-block` `<span>` so it never
 *     disrupts the surrounding layout. Children remain focusable;
 *     the transform is purely visual and never moves an element
 *     out of its hit-box meaningfully (max ~6px default pull).
 *
 * Defaults are tuned for the V4 hero CTAs:
 *   radius = 64px (visual influence zone)
 *   pull   = 6px  (maximum translate magnitude)
 */
export interface MagneticCtaProps {
  children: ReactNode;
  /** Pixel radius from the wrapper's center within which the pull engages. Default 64. */
  radius?: number;
  /** Maximum translate distance, in pixels, in either axis. Default 6. */
  pull?: number;
  className?: string;
  style?: CSSProperties;
}

export function MagneticCta({
  children,
  radius = 64,
  pull = 6,
  className,
  style,
}: MagneticCtaProps) {
  const wrapRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el || typeof window === 'undefined') return;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (reduced.matches) return;

    let frame = 0;
    let tx = 0;
    let ty = 0;

    const apply = () => {
      if (el) {
        el.style.transform = `translate(${tx.toFixed(2)}px, ${ty.toFixed(2)}px)`;
      }
    };

    const onMove = (e: PointerEvent) => {
      const rect = el.getBoundingClientRect();
      const cx = rect.left + rect.width / 2;
      const cy = rect.top + rect.height / 2;
      const dx = e.clientX - cx;
      const dy = e.clientY - cy;
      const dist = Math.hypot(dx, dy);
      if (dist < radius) {
        // Scale pull magnitude by distance — fully engaged at center,
        // tapering toward zero at the radius edge. Cap at `pull`.
        const strength = 1 - dist / radius; // 0..1
        tx = Math.max(-pull, Math.min(pull, dx * 0.22 * strength * (pull / 6)));
        ty = Math.max(-pull, Math.min(pull, dy * 0.22 * strength * (pull / 6)));
      } else if (tx !== 0 || ty !== 0) {
        tx = 0;
        ty = 0;
      } else {
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };

    const onLeave = () => {
      tx = 0;
      ty = 0;
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerleave', onLeave);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerleave', onLeave);
      cancelAnimationFrame(frame);
    };
  }, [radius, pull]);

  return (
    <span
      ref={wrapRef}
      className={className}
      style={{
        display: 'inline-block',
        transition: 'transform 80ms ease-out',
        willChange: 'transform',
        ...style,
      }}
    >
      {children}
    </span>
  );
}

export default MagneticCta;
